import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Inline text link — primary-coloured (lighter purple in dark). Renders an
 * <a> when `href` is given, otherwise a <button> for in-card actions.
 */
export function AuthLink({
  children,
  icon: Icon,
  onClick,
  href,
  className,
}: {
  children: ReactNode;
  icon?: LucideIcon;
  onClick?: () => void;
  href?: string;
  className?: string;
}) {
  const cls = cn(
    'inline-flex cursor-pointer items-center gap-1.5 rounded text-[13px] font-semibold text-primary hover:underline dark:text-[#c084fc]',
    // ANA-154: keyboard focus must be visible on the glass surface — explicit,
    // on-brand ring rather than the inconsistent UA default outline.
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
    className,
  );
  const inner = (
    <>
      {Icon && <Icon className="size-3.5" strokeWidth={1.9} aria-hidden="true" />}
      {children}
    </>
  );
  if (href) {
    return (
      <a href={href} className={cls}>
        {inner}
      </a>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {inner}
    </button>
  );
}
